/**
 * PythonImportsHelper - Helper for analyzing and managing Python imports
 *
 * This helper provides functionality to analyze import dependencies,
 * track imported modules, and manage import-related operations.
 */

import { runPythonIsolated } from '../helpers/python-isolated.helper';


export interface ImportInfo {
  name: string;
  alias?: string;
  from?: string;
  isAvailable: boolean;
  type: 'import' | 'from_import' | 'relative_import';
  line?: number;
}

export interface ImportAnalysisResult {
  imports: ImportInfo[];
  dependencies: string[];
  missingModules: string[];
  errors: string[];
  totalImports: number;
}

export interface ModuleInfo {
  name: string;
  version?: string;
  location?: string;
  isBuiltin: boolean;
  isInstalled: boolean;
  submodules?: string[];
}

export interface ImportsConfig {
  enableLogging?: boolean;
  checkAvailability?: boolean;
  includeBuiltins?: boolean;
  trackUsage?: boolean;
}

export class PythonImportsHelper {
  private readonly python: any;
  private config: ImportsConfig;
  private readonly importUsageTracker = new Map<string, number>();

  constructor(python: any, config: ImportsConfig = {}) {
    this.python = python;
    this.config = {
      enableLogging: false,
      checkAvailability: true,
      includeBuiltins: true,
      trackUsage: true,
      ...config
    };
  }

  /**
   * Analyze imports in Python code
   */
  analyzeImports(code: string): ImportAnalysisResult {
    try {

      // Pyodide built-in modules that are available at runtime but NOT
      // discoverable via importlib.util.find_spec(). Attempting to
      // micropip.install() these hangs or installs wrong packages.
      const skipModules = [
        'js',           // Pyodide JS interop bridge
        'pyodide',      // Pyodide runtime
        'pyodide_js',   // Pyodide JS utilities
      ];

      const analysisCode = `
import ast
import json
import sys
import importlib.util

# Pyodide built-in modules — always available at runtime, skip install checks.
# These return None from find_spec() but are importable. Includes root packages
# only; dotted paths like 'pyodide.ffi' are handled by root_package extraction.
SKIP_MODULES = set(${JSON.stringify(skipModules)})

def analyze_imports(code_str):
    result = {
        'imports': [],
        'dependencies': [],
        'missing_modules': [],
        'errors': [],
        'total_imports': 0
    }

    try:
        # Parse the code into AST using compile() with
        # PyCF_ALLOW_TOP_LEVEL_AWAIT so that top-level await
        # (common in Pyodide step code) does not cause a SyntaxError.
        tree = compile(code_str, '<string>', 'exec',
                       flags=ast.PyCF_ONLY_AST | ast.PyCF_ALLOW_TOP_LEVEL_AWAIT)

        for node in ast.walk(tree):
            if isinstance(node, ast.Import):
                for alias in node.names:
                    import_info = {
                        'name': alias.name,
                        'alias': alias.asname,
                        'type': 'import',
                        'line': node.lineno,
                        'is_available': False
                    }

                    # Skip Pyodide built-in modules — always available at runtime
                    if alias.name in SKIP_MODULES:
                        import_info['is_available'] = True
                        result['imports'].append(import_info)
                        result['dependencies'].append(alias.name)
                        continue

                    # Check if module is available
                    try:
                        spec = importlib.util.find_spec(alias.name)
                        import_info['is_available'] = spec is not None
                    except (ImportError, ModuleNotFoundError, AttributeError):
                        import_info['is_available'] = False
                        result['missing_modules'].append(alias.name)

                    result['imports'].append(import_info)
                    result['dependencies'].append(alias.name)

            elif isinstance(node, ast.ImportFrom):
                from_module = node.module or ''
                level = node.level

                for alias in node.names:
                    import_info = {
                        'name': alias.name,
                        'alias': alias.asname,
                        'from': from_module,
                        'type': 'relative_import' if level > 0 else 'from_import',
                        'line': node.lineno,
                        'is_available': False
                    }

                    # Check if module/attribute is available
                    try:
                        if from_module:
                            # Extract root package for skip check (e.g., 'pyodide.ffi' -> 'pyodide')
                            root_package = from_module.split('.')[0]

                            # Skip Pyodide built-in modules
                            if root_package in SKIP_MODULES:
                                import_info['is_available'] = True
                                if root_package not in result['dependencies']:
                                    result['dependencies'].append(root_package)
                                result['imports'].append(import_info)
                                continue

                            spec = importlib.util.find_spec(from_module)
                            import_info['is_available'] = spec is not None
                            if from_module not in result['dependencies']:
                                result['dependencies'].append(from_module)
                        else:
                            import_info['is_available'] = False
                    except (ImportError, ModuleNotFoundError, AttributeError):
                        import_info['is_available'] = False
                        if from_module and from_module not in result['missing_modules']:
                            result['missing_modules'].append(from_module)

                    result['imports'].append(import_info)

        result['total_imports'] = len(result['imports'])

    except SyntaxError as e:
        result['errors'].append(f"Syntax error: {str(e)}")
    except Exception as e:
        result['errors'].append(f"Analysis error: {str(e)}")

    return result

# Analyze the provided code
code_to_analyze = ${JSON.stringify(code)}
analysis_result = analyze_imports(code_to_analyze)
json.dumps(analysis_result)
      `;

      // Isolated so the analyzer's names never reach step code (EHQ-117).
      const resultJson = runPythonIsolated(this.python, analysisCode);
      const analysis = JSON.parse(resultJson);

      // Track import usage if enabled
      if (this.config.trackUsage) {
        analysis.dependencies.forEach((dep: string) => {
          const currentCount = this.importUsageTracker.get(dep) || 0;
          this.importUsageTracker.set(dep, currentCount + 1);
        });
      }


      return {
        imports: analysis.imports || [],
        dependencies: analysis.dependencies || [],
        missingModules: analysis.missing_modules || [],
        errors: analysis.errors || [],
        totalImports: analysis.total_imports || 0
      };

    } catch (error: any) {
      return {
        imports: [],
        dependencies: [],
        missingModules: [],
        errors: [error.message],
        totalImports: 0
      };
    }
  }

  /**
   * Get information about a specific module
   */
  getModuleInfo(moduleName: string): ModuleInfo {
    try {

      const moduleInfoCode = `
import sys
import importlib.util
import json

def get_module_info(module_name):
    result = {
        'name': module_name,
        'version': None,
        'location': None,
        'is_builtin': False,
        'is_installed': False,
        'submodules': []
    }

    try:
        # Check if module is available
        spec = importlib.util.find_spec(module_name)
        if spec:
            result['is_installed'] = True
            result['location'] = str(spec.origin) if spec.origin else None

            # Check if it's a builtin module
            result['is_builtin'] = module_name in sys.builtin_module_names

            # Try to import and get version
            try:
                module = importlib.import_module(module_name)

                # Try different version attributes
                if hasattr(module, '__version__'):
                    result['version'] = module.__version__
                elif hasattr(module, 'VERSION'):
                    result['version'] = str(module.VERSION)
                elif hasattr(module, 'version'):
                    result['version'] = str(module.version)

                # Get submodules if it's a package
                if hasattr(module, '__path__'):
                    try:
                        import pkgutil
                        submodules = []
                        for importer, modname, ispkg in pkgutil.iter_modules(module.__path__):
                            submodules.append(modname)
                        result['submodules'] = submodules[:10]  # Limit to 10 submodules
                    except:
                        pass

            except ImportError:
                pass

    except (ImportError, ModuleNotFoundError, AttributeError):
        pass

    return result

module_info = get_module_info('${moduleName}')
json.dumps(module_info)
      `;

      const resultJson = runPythonIsolated(this.python, moduleInfoCode);
      const moduleInfo = JSON.parse(resultJson);

      return {
        name: moduleInfo.name,
        version: moduleInfo.version,
        location: moduleInfo.location,
        isBuiltin: moduleInfo.is_builtin,
        isInstalled: moduleInfo.is_installed,
        submodules: moduleInfo.submodules || []
      };

    } catch (_error: any) {
      return {
        name: moduleName,
        isBuiltin: false,
        isInstalled: false
      };
    }
  }

  /**
   * Get list of all available modules
   */
  getAvailableModules(): string[] {
    try {
      const modulesCode = `
import sys
import pkgutil
import json

def get_available_modules():
    modules = set()

    # Add builtin modules
    modules.update(sys.builtin_module_names)

    # Add installed modules
    try:
        for importer, modname, ispkg in pkgutil.iter_modules():
            modules.add(modname)
    except:
        pass

    # Add modules from sys.modules
    try:
        for name in sys.modules.keys():
            if '.' not in name:  # Only top-level modules
                modules.add(name)
    except:
        pass

    return sorted(list(modules))

available_modules = get_available_modules()
json.dumps(available_modules)
      `;

      const resultJson = runPythonIsolated(this.python, modulesCode);
      return JSON.parse(resultJson);

    } catch (error: any) {
      console.warn('Python imports: failed to list available modules', error);
      return [];
    }
  }

  /**
   * Check if all imports in code are available
   */
  checkImportAvailability(code: string): {
    allAvailable: boolean;
    missingModules: string[];
    availableModules: string[];
  } {
    const analysis = this.analyzeImports(code);

    return {
      allAvailable: analysis.missingModules.length === 0,
      missingModules: analysis.missingModules,
      availableModules: analysis.dependencies.filter(dep =>
        !analysis.missingModules.includes(dep)
      )
    };
  }

  /**
   * Get import suggestions for missing modules
   */
  getImportSuggestions(missingModule: string): string[] {
    const suggestions: string[] = [];

    // Common module name mappings
    const moduleMapping: { [key: string]: string[] } = {
      'cv2': ['opencv-python'],
      'PIL': ['Pillow'],
      'sklearn': ['scikit-learn'],
      'bs4': ['beautifulsoup4'],
      'requests': ['requests'],
      'numpy': ['numpy'],
      'pandas': ['pandas'],
      'matplotlib': ['matplotlib'],
      'scipy': ['scipy'],
      'torch': ['torch'],
      'tensorflow': ['tensorflow'],
      'plotly': ['plotly'],
      'seaborn': ['seaborn'],
      'flask': ['Flask'],
      'django': ['Django']
    };

    if (moduleMapping[missingModule]) {
      suggestions.push(...moduleMapping[missingModule]);
    } else {
      // Generic suggestion
      suggestions.push(missingModule);
    }

    // Check for similar module names
    const availableModules = this.getAvailableModules();
    const similarModules = availableModules.filter(mod =>
      mod.toLowerCase().includes(missingModule.toLowerCase()) ||
      missingModule.toLowerCase().includes(mod.toLowerCase())
    );

    suggestions.push(...similarModules);

    return [...new Set(suggestions)]; // Remove duplicates
  }


  /**
   * Get import usage statistics
   */
  getImportUsageStats(): { module: string; count: number }[] {
    return Array.from(this.importUsageTracker.entries())
      .map(([module, count]) => ({ module, count }))
      .sort((a, b) => b.count - a.count);
  }

  /**
   * Clear import usage tracking
   */
  clearImportUsageTracking(): void {
    this.importUsageTracker.clear();
  }

  /**
   * Check if Python runtime is ready
   */
  isRuntimeReady(): boolean {
    return !!(this.python && typeof this.python.runPython === 'function');
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<ImportsConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): ImportsConfig {
    return { ...this.config };
  }

  /**
   * Generate import recommendations based on code content
   */
  generateImportRecommendations(code: string): string[] {
    const recommendations: string[] = [];

    // Patterns that suggest specific imports
    const patterns = [
      { pattern: /\bnp\./g, module: 'numpy' },
      { pattern: /\bpd\./g, module: 'pandas' },
      { pattern: /\bplt\./g, module: 'matplotlib.pyplot' },
      { pattern: /\bsns\./g, module: 'seaborn' },
      { pattern: /\bos\.path\.|os\.getcwd\(|os\.listdir\(/g, module: 'os' },
      { pattern: /\bsys\.path\.|sys\.argv/g, module: 'sys' },
      { pattern: /\bmath\./g, module: 'math' },
      { pattern: /\brandom\./g, module: 'random' },
      { pattern: /\btime\./g, module: 'time' },
      { pattern: /\bjson\./g, module: 'json' },
      { pattern: /\bre\./g, module: 're' },
      { pattern: /\bdatetime\./g, module: 'datetime' }
    ];

    patterns.forEach(({ pattern, module }) => {
      if (pattern.test(code)) {
        recommendations.push(module);
      }
    });

    return [...new Set(recommendations)];
  }

  /**
   * Validate import statement syntax
   */
  validateImportSyntax(importStatement: string): {
    isValid: boolean;
    error?: string;
    suggestion?: string;
  } {
    try {
      // Basic syntax validation using simplified regex patterns
      const _moduleNamePattern = /[\w.]+/;
      const _aliasPattern = /\s+as\s+\w+/;
      const _importItemPattern = /(\w+|\*)/;

      // Simplified patterns with reduced complexity
      const simpleImportPattern = /^import\s+[\w.]+/;
      const fromImportPattern = /^from\s+[\w.]+\s+import\s+/;

      const importPatterns = [
        simpleImportPattern,
        fromImportPattern
      ];

      const isValid = importPatterns.some(pattern => pattern.test(importStatement.trim()));

      if (!isValid) {
        return {
          isValid: false,
          error: 'Invalid import statement syntax',
          suggestion: 'Use: import module or from module import item'
        };
      }

      return { isValid: true };

    } catch (error: any) {
      return {
        isValid: false,
        error: error.message
      };
    }
  }


}
